/**
 * CREACIÓN VALIDADA DE MUTACIONES LOCALES (FASE 5.2 §8)
 *
 * Capa fina sobre `offline/db.ts`: aplica las comprobaciones de "¿tiene
 * sentido intentar esto ahora?" ANTES de persistir — rol, TTL, existencia
 * en el paquete local, OT no terminal, y la secuencia real vía
 * `proyectarEstadoLinea`. Son precomprobaciones de UX, NUNCA autoridad:
 * el servidor (FASE 5.3+) las vuelve a hacer todas, con datos frescos, al
 * sincronizar — exactamente igual que `accionesPermitidas` en el backend
 * es un hint y nunca sustituye la validación real de
 * `fn_ot_linea_evento_registrar`.
 *
 * Esta misma validación de secuencia es también la protección contra
 * doble-click (encargo §12): un segundo intento del MISMO tipo sobre la
 * MISMA línea, mientras el primero sigue PENDIENTE, ya no es una
 * transición válida desde el estado proyectado — se rechaza aquí, en la
 * capa de persistencia, no sólo porque el botón esté deshabilitado.
 */
import {
  descartarMutacion as descartarMutacionDb,
  guardarBlobEvidencia,
  insertarMutacion,
  leerLineaLocal,
  leerMeta,
  leerMutacion,
  leerOtLocal,
  listarMutacionesDeLinea,
  listarMutacionesDeOt,
  type Mutacion,
  type PayloadEvidenciaSubir,
  type PayloadMutacion,
  type PayloadNcCerrar,
  type PayloadNcCrear,
  type PayloadNcIniciarTratamiento,
  type TipoMutacionLinea,
  type TipoMutacionNc,
} from './db.ts';
import { decidirPorCaducidad, lineaIniciadaEnDispositivo, nivelCaducidad } from './caducidad.ts';
import { esMutacionActiva, esRehacer, esTipoMutacionLinea, proyectarEstadoLinea, transicionValida } from './proyeccion.ts';
import { sincronizarPendientes } from './sync.ts';

export type ResultadoCrearMutacion =
  /** `aviso`: F14.2 — p. ej. «Los datos descargados pueden estar desactualizados» (24–72 h). */
  | { readonly ok: true; readonly mutacion: Mutacion; readonly aviso?: string | null }
  | { readonly ok: false; readonly motivo: string };

export interface DatosNuevaMutacion {
  readonly tipo: TipoMutacionLinea;
  readonly idOt: string;
  readonly idLinea: string;
  readonly payload: PayloadMutacion;
  /** ISO — momento real de la acción, tal como lo confirma quien la registra. */
  readonly fechaEfectiva: string;
  /** F14.2 — sólo cuando la MISMA acción ya se intentó en línea sin respuesta (ver `db.ts::insertarMutacion`). */
  readonly idOperacionCliente?: string;
  /** F14.2 — «Confirmar limpieza · Habitación 305» (`db.ts::Mutacion.rotuloLocal`). */
  readonly rotuloLocal?: string | null;
}

const ESTADOS_OT_TERMINALES = new Set(['ANULADA', 'NO_REALIZADA']);

type Rechazo = { readonly ok: false; readonly motivo: string };

/**
 * §8, reutilizado tal cual por NC y evidencias (FASE 5.4): gestionar (verificar, NC, evidencias de gestión) es del
 * ENCARGADO — mismo criterio que el backend.
 */
function exigirEncargado(roles: readonly { readonly rol: string }[]): Rechazo | null {
  if (!roles.some((r) => r.rol === 'ENCARGADO')) {
    return { ok: false, motivo: 'Sólo un encargado puede registrar esta acción sin conexión.' };
  }
  return null;
}

/**
 * F14.2 — lo que el OPERARIO ejecuta de SUS tareas (`sync/servicio.ts::TIPOS_EJECUCION_PROPIA` en el backend):
 * confirmar (y rehacer), No conforme y No aplica. Verificar nunca. El paquete del operario ya sólo trae sus líneas;
 * el servidor lo revalida todo al sincronizar.
 */
const TIPOS_EJECUCION_PROPIA: ReadonlySet<TipoMutacionLinea> = new Set(['LINEA_CONFIRMAR', 'LINEA_NO_OK', 'LINEA_NO_APLICA']);

function exigirCapacidadLinea(roles: readonly { readonly rol: string }[], tipo: TipoMutacionLinea): Rechazo | null {
  if (roles.some((r) => r.rol === 'ENCARGADO')) return null;
  if (roles.some((r) => r.rol === 'OPERARIO') && TIPOS_EJECUCION_PROPIA.has(tipo)) return null;
  return { ok: false, motivo: tipo === 'LINEA_VERIFICAR' ? 'Verificar es tarea del encargado.' : 'Tu perfil no puede registrar esta acción sin conexión.' };
}

/**
 * Sobre qué unidad de trabajo actúa una operación, para la caducidad (`caducidad.ts`, «TRABAJO INICIADO»):
 *   LINEA   acción o foto de una línea — sólo cuenta ESA línea;
 *   LINEAS  NC nueva sobre líneas concretas — TODAS tienen que estar iniciadas;
 *   OT      foto de la OT o NC de toda la OT — alguna línea iniciada (no inicia ninguna);
 *   NC      transición o foto de una NC que ya está en el dispositivo — trabajo en curso.
 */
type UnidadTrabajo =
  | { readonly tipo: 'LINEA'; readonly idLinea: string }
  | { readonly tipo: 'LINEAS'; readonly idsLineas: readonly string[] }
  | { readonly tipo: 'OT' }
  | { readonly tipo: 'NC' };

async function lineaIniciada(idPerfil: string, idLinea: string): Promise<boolean> {
  const [linea, operaciones] = await Promise.all([leerLineaLocal(idPerfil, idLinea), listarMutacionesDeLinea(idPerfil, idLinea)]);
  return lineaIniciadaEnDispositivo(linea, operaciones);
}

async function unidadIniciada(idPerfil: string, idOt: string, unidad: UnidadTrabajo): Promise<boolean> {
  switch (unidad.tipo) {
    case 'NC':
      return true;
    case 'LINEA':
      return lineaIniciada(idPerfil, unidad.idLinea);
    case 'LINEAS':
      if (unidad.idsLineas.length === 0) return unidadIniciada(idPerfil, idOt, { tipo: 'OT' });
      return (await Promise.all(unidad.idsLineas.map((id) => lineaIniciada(idPerfil, id)))).every(Boolean);
    case 'OT': {
      const [detalle, operaciones] = await Promise.all([leerOtLocal(idPerfil, idOt), listarMutacionesDeOt(idPerfil, idOt)]);
      return (detalle?.lineas ?? []).some((l) => lineaIniciadaEnDispositivo(l, operaciones.filter((m) => m.idEntidad === l.id)));
    }
  }
}

/**
 * Datos preparados + rol del PERFIL + caducidad (F14.2, `caducidad.ts`), comunes a línea/NC/evidencia.
 *
 * Rol: además de los roles que pasa quien llama (la pantalla), la capacidad se exige al rol del propio perfil
 * (`meta.rol`, el del paquete que el servidor le dio). Una pantalla desfasada (p. ej. un render con los roles del
 * perfil ANTERIOR tras cambiar de perfil, OFF-F142-ROLES-AL-CAMBIAR-PERFIL) nunca puede dejar en la cola de un
 * OPERARIO una acción de encargado.
 *
 * Caducidad: hasta F14.2 era un corte único a las 24 h; ahora: <24 h normal, 24–72 h con aviso, ≥72 h sólo sobre
 * trabajo ya iniciado en este dispositivo, medido por UNIDAD (la línea, nunca la OT entera).
 */
async function comprobarCaducidad(
  idPerfil: string,
  idOt: string,
  unidad: UnidadTrabajo,
  capacidad: (roles: readonly { readonly rol: string }[]) => Rechazo | null,
): Promise<Rechazo | { readonly ok: true; readonly aviso: string | null }> {
  const meta = await leerMeta(idPerfil);
  if (meta === null) {
    return { ok: false, motivo: 'Todavía no has preparado datos para trabajar sin conexión.' };
  }
  const faltaRolDelPerfil = capacidad([{ rol: meta.rol }]);
  if (faltaRolDelPerfil !== null) return faltaRolDelPerfil;
  const nivel = nivelCaducidad(meta);
  const iniciada = nivel === 'CADUCADO' ? await unidadIniciada(idPerfil, idOt, unidad) : false;
  const decision = decidirPorCaducidad(nivel, iniciada);
  return decision.permitido ? { ok: true, aviso: decision.aviso } : { ok: false, motivo: decision.motivo };
}

export async function crearMutacionLinea(
  idPerfil: string,
  roles: readonly { readonly rol: string }[],
  datos: DatosNuevaMutacion,
): Promise<ResultadoCrearMutacion> {
  //  §8/§9: capacidad por tipo (ENCARGADO todo; OPERARIO su ejecución, F14.2) + caducidad — nunca la UI como única
  //  barrera, la capa de persistencia repite la comprobación.
  const faltaRol = exigirCapacidadLinea(roles, datos.tipo);
  if (faltaRol !== null) return faltaRol;
  const caducidad = await comprobarCaducidad(idPerfil, datos.idOt, { tipo: 'LINEA', idLinea: datos.idLinea }, (r) => exigirCapacidadLinea(r, datos.tipo));
  if (!caducidad.ok) return caducidad;

  const detalleOt = await leerOtLocal(idPerfil, datos.idOt);
  if (detalleOt === null) {
    return { ok: false, motivo: 'Esa orden de trabajo no está disponible sin conexión.' };
  }
  if (ESTADOS_OT_TERMINALES.has(detalleOt.ot.estado)) {
    return { ok: false, motivo: `La OT está ${detalleOt.ot.estado} y no admite nuevas acciones.` };
  }

  const linea = await leerLineaLocal(idPerfil, datos.idLinea);
  if (linea === null || linea.idOt !== datos.idOt) {
    return { ok: false, motivo: 'Esa línea no está disponible sin conexión.' };
  }

  //  §10/§11 — el estado que importa es el PROYECTADO (snapshot + cola
  //  activa de ESTA línea), nunca sólo el snapshot del servidor: si ya
  //  hay un CONFIRMAR activo, VERIFICAR debe poder registrarse encima.
  //  "Activa" (FASE 5.3 §14) es PENDIENTE o ERROR_REINTENTABLE — ver
  //  `esMutacionActiva` — nunca sólo PENDIENTE a secas desde 5.3: un
  //  fallo transitorio de sincronización no resuelve nada todavía.
  //  `listarMutacionesDeLinea` indexa por `idEntidad`: una `EVIDENCIA_SUBIR`
  //  con ámbito LINEA sobre esta MISMA línea comparte ese índice sin
  //  compartir nada de la máquina de estados — `esTipoMutacionLinea` es lo
  //  que la excluye antes de proyectar (FASE 5.4 §14).
  const mutacionesDeLinea = (await listarMutacionesDeLinea(idPerfil, datos.idLinea)).filter((m) =>
    esTipoMutacionLinea(m.tipo),
  );
  const activasDeLinea = mutacionesDeLinea.filter(esMutacionActiva);
  const estadoProyectado = proyectarEstadoLinea(linea.estado, activasDeLinea);

  if (!transicionValida(estadoProyectado, datos.tipo)) {
    return {
      ok: false,
      motivo: `Esa acción no se puede registrar ahora: la línea está ${estadoProyectado}.`,
    };
  }

  //  F14.2 — REHACER (CONFIRMAR desde NO_OK): las dos condiciones de 0077, aquí sólo como precomprobación con lo
  //  que sabe el paquete. Una NC creada o anulada después de descargar la decide el servidor al sincronizar.
  if (esRehacer(estadoProyectado, datos.tipo)) {
    if (linea.tieneNc !== true) {
      return { ok: false, motivo: 'Para rehacer esta tarea hace falta una no conformidad abierta sobre ella. Habla con tu encargado.' };
    }
    const correccion = (datos.payload as { observacion?: string | null }).observacion;
    if (typeof correccion !== 'string' || correccion.trim() === '') {
      return { ok: false, motivo: 'Para rehacer, explica qué has corregido.' };
    }
  }

  //  §14 — si ya hay una mutación activa sobre esta MISMA línea, la nueva
  //  depende causalmente de ella (es la única transición que
  //  `transicionValida` acaba de aceptar desde el estado que esa mutación
  //  deja proyectado). `activasDeLinea` ya viene en `ordenLocal` —
  //  `listarMutacionesDeLinea` lo garantiza — así que el último elemento
  //  es la más reciente.
  const dependeDe = activasDeLinea.at(-1)?.idOperacionCliente ?? null;

  const mutacion = await insertarMutacion(idPerfil, {
    tipo: datos.tipo,
    idEntidad: datos.idLinea,
    idOt: datos.idOt,
    idCentro: detalleOt.ot.idCentro,
    payload: datos.payload,
    //  §6 — la revisión que la línea tenía EN ESTE MOMENTO, copiada tal
    //  cual del paquete local. Nunca recalculada, nunca leída de red.
    revisionBase: linea.revisionBase,
    fechaEfectiva: datos.fechaEfectiva,
    dependeDe,
    ...(datos.idOperacionCliente === undefined ? {} : { idOperacionCliente: datos.idOperacionCliente }),
    rotuloLocal: datos.rotuloLocal ?? null,
    estadoOrigenLocal: estadoProyectado,
  });

  //  FASE 5.3 §16/auditoría punto 3 — "acción → cola → sync", nunca
  //  "acción → cola → esperar a otro disparador si ya hay red ahora
  //  mismo". Encolar es SIEMPRE lo primero (ya ocurrió, arriba) y esto
  //  nunca lo sustituye: si la petición falla, la mutación se queda
  //  exactamente igual de PENDIENTE que si esto no se hubiera llamado —
  //  `sincronizarPendientes` ya es información-y-mutex, nunca la única
  //  vía de persistencia. Sin esperar (`void`): quien llama a
  //  `crearMutacionLinea` recibe su resultado al instante, offline u
  //  online, y el intento de sincronizar sigue en segundo plano.
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void sincronizarPendientes(idPerfil);
  }

  return { ok: true, mutacion, aviso: caducidad.aviso };
}

/** `false` sin lanzar si ya no existe o ya no está PENDIENTE — quien llama decide el mensaje. */
export async function descartarMutacionPendiente(idPerfil: string, idOperacionCliente: string): Promise<boolean> {
  return descartarMutacionDb(idPerfil, idOperacionCliente);
}

/** Mismo criterio que el final de `crearMutacionLinea` (§16) — sin esperar, la cola ya quedó persistida antes de esto. */
function dispararSyncSiOnline(idPerfil: string): void {
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void sincronizarPendientes(idPerfil);
  }
}

// ==================================================== NC offline (FASE 5.4)

export interface DatosNuevaMutacionNcCrear {
  readonly idOt: string;
  readonly descripcion: string;
  readonly gravedad: 'LEVE' | 'GRAVE' | 'CRITICA';
  readonly idsLineas?: readonly string[];
  /** ISO — momento real en que se detectó la NC. */
  readonly fechaEfectiva: string;
}

/**
 * §3/§4 FASE 5.4: crear una NC offline (TODA_OT o sobre líneas concretas de
 * la MISMA OT). Sin `revisionBase` ni proyección de estado — NC no tiene el
 * equivalente de la máquina PENDIENTE→CONFIRMADA→VERIFICADA de línea, y el
 * servidor (`fn_nc_registrar`) es la única autoridad real (mismo criterio
 * que `sync/servicio.ts::procesarMutacionNc` en el backend).
 */
export async function crearMutacionNcCrear(
  idPerfil: string,
  roles: readonly { readonly rol: string }[],
  datos: DatosNuevaMutacionNcCrear,
): Promise<ResultadoCrearMutacion> {
  const faltaRol = exigirEncargado(roles);
  if (faltaRol !== null) return faltaRol;
  const caducidad = await comprobarCaducidad(idPerfil, datos.idOt, { tipo: 'LINEAS', idsLineas: datos.idsLineas ?? [] }, exigirEncargado);
  if (!caducidad.ok) return caducidad;

  const detalleOt = await leerOtLocal(idPerfil, datos.idOt);
  if (detalleOt === null) {
    return { ok: false, motivo: 'Esa orden de trabajo no está disponible sin conexión.' };
  }
  if (ESTADOS_OT_TERMINALES.has(detalleOt.ot.estado)) {
    return { ok: false, motivo: `La OT está ${detalleOt.ot.estado} y no admite nuevas NC.` };
  }
  if (datos.descripcion.trim() === '') {
    return { ok: false, motivo: 'La descripción de la NC es obligatoria.' };
  }

  const payload: PayloadNcCrear = {
    descripcion: datos.descripcion.trim(),
    gravedad: datos.gravedad,
    idsLineas: datos.idsLineas,
  };
  const mutacion = await insertarMutacion(idPerfil, {
    tipo: 'NC_CREAR',
    idEntidad: datos.idOt,
    idOt: datos.idOt,
    idCentro: detalleOt.ot.idCentro,
    payload,
    revisionBase: null,
    fechaEfectiva: datos.fechaEfectiva,
    dependeDe: null,
  });
  dispararSyncSiOnline(idPerfil);
  return { ok: true, mutacion };
}

/**
 * Resuelve el destino (`idEntidad`/`dependeDe`) de una transición NC o de
 * una evidencia sobre NC a partir de la ÚLTIMA mutación local conocida de
 * ESA NC (`idOperacionClienteActual` — la UI ya sabe cuál es: es la fila
 * que está mostrando). Réplica del mismo razonamiento dual que
 * `sync/servicio.ts::resolverIdNcDependencia` en el backend, pero del lado
 * cliente y ANTES de sincronizar: si el servidor ya resolvió esa mutación
 * (`SINCRONIZADA` + `resultadoNc`), se usa el `idNc` real directamente,
 * `dependeDe` es `null` Y `revisionBase` viaja como el `idHistorial` real
 * conocido (PRE-COMMIT REVIEW FASE 5.4 punto 1 — sin esto, CUALQUIER
 * transición sobre una NC ya sincronizada en una sesión ANTERIOR llegaría
 * siempre con `revisionBase: null`, y el servidor la marcaría CONFLICTO
 * SIEMPRE, incluso sin haber cambiado nada); si sigue activa
 * (`PENDIENTE`/`ERROR_REINTENTABLE`), se encadena por `dependeDe` — igual
 * que CONFIRMAR→VERIFICAR en líneas (§12/§14), y `revisionBase` viaja
 * como `null` porque el servidor la ignora en favor del rebase. Un estado
 * RECHAZADA/EN_ESPERA se informa como bloqueo: nunca se construye una
 * mutación nueva encima de una que ya no tiene camino hacia adelante.
 */
async function resolverDestinoNc(
  idPerfil: string,
  idOperacionClienteActual: string,
): Promise<
  | { readonly ok: true; readonly idEntidad: string; readonly dependeDe: string | null; readonly revisionBase: string | null }
  | { readonly ok: false; readonly motivo: string }
> {
  const actual = await leerMutacion(idPerfil, idOperacionClienteActual);
  if (actual === null) {
    return { ok: false, motivo: 'Esa no conformidad ya no está disponible sin conexión.' };
  }
  if (actual.resultadoNc != null) {
    return { ok: true, idEntidad: actual.resultadoNc.idNc, dependeDe: null, revisionBase: actual.resultadoNc.idHistorial };
  }
  if (actual.estadoSync === 'PENDIENTE' || actual.estadoSync === 'ERROR_REINTENTABLE') {
    return { ok: true, idEntidad: actual.idOperacionCliente, dependeDe: actual.idOperacionCliente, revisionBase: null };
  }
  if (actual.estadoSync === 'RECHAZADA') {
    return { ok: false, motivo: 'Esa acción sobre la NC fue rechazada por el servidor; no se puede continuar sobre ella.' };
  }
  return { ok: false, motivo: 'Esa acción sobre la NC sigue pendiente de resolución; espera a que se resuelva antes de continuar.' };
}

interface DatosNuevaMutacionNcTransicionBase {
  readonly idOt: string;
  /** idOperacionCliente de la ÚLTIMA mutación local conocida de esta NC (su CREAR, o su INICIAR/CERRAR más reciente). */
  readonly idOperacionClienteNc: string;
  readonly fechaEfectiva: string;
}

async function crearMutacionNcTransicion(
  idPerfil: string,
  roles: readonly { readonly rol: string }[],
  tipo: TipoMutacionNc,
  datos: DatosNuevaMutacionNcTransicionBase,
  payload: PayloadNcIniciarTratamiento | PayloadNcCerrar,
): Promise<ResultadoCrearMutacion> {
  const faltaRol = exigirEncargado(roles);
  if (faltaRol !== null) return faltaRol;
  const caducidad = await comprobarCaducidad(idPerfil, datos.idOt, { tipo: 'NC' }, exigirEncargado);
  if (!caducidad.ok) return caducidad;

  const detalleOt = await leerOtLocal(idPerfil, datos.idOt);
  if (detalleOt === null) {
    return { ok: false, motivo: 'Esa orden de trabajo no está disponible sin conexión.' };
  }

  const destino = await resolverDestinoNc(idPerfil, datos.idOperacionClienteNc);
  if (!destino.ok) return destino;

  const mutacion = await insertarMutacion(idPerfil, {
    tipo,
    idEntidad: destino.idEntidad,
    idOt: datos.idOt,
    idCentro: detalleOt.ot.idCentro,
    payload,
    revisionBase: destino.revisionBase,
    fechaEfectiva: datos.fechaEfectiva,
    dependeDe: destino.dependeDe,
  });
  dispararSyncSiOnline(idPerfil);
  return { ok: true, mutacion };
}

export async function crearMutacionNcIniciarTratamiento(
  idPerfil: string,
  roles: readonly { readonly rol: string }[],
  datos: DatosNuevaMutacionNcTransicionBase & { readonly motivo: string | null },
): Promise<ResultadoCrearMutacion> {
  return crearMutacionNcTransicion(idPerfil, roles, 'NC_INICIAR_TRATAMIENTO', datos, { motivo: datos.motivo });
}

export async function crearMutacionNcCerrar(
  idPerfil: string,
  roles: readonly { readonly rol: string }[],
  datos: DatosNuevaMutacionNcTransicionBase & { readonly accionCorrectiva: string },
): Promise<ResultadoCrearMutacion> {
  if (datos.accionCorrectiva.trim() === '') {
    return { ok: false, motivo: 'CERRAR exige una acción correctiva.' };
  }
  return crearMutacionNcTransicion(idPerfil, roles, 'NC_CERRAR', datos, {
    accionCorrectiva: datos.accionCorrectiva.trim(),
  });
}

// ============================================== Evidencias offline (FASE 5.4)

/** Mismo límite que `LIMITE_EVIDENCIA_BYTES` (backend `almacenamiento/evidencias.ts`) — conveniencia de UI, nunca autoridad. */
export const LIMITE_EVIDENCIA_BYTES = 10 * 1024 * 1024;

/** Por debajo de este umbral no merece la pena comprimir — una foto ya pequeña sólo perdería calidad sin ahorro real. */
const UMBRAL_COMPRESION_BYTES = 800 * 1024;
const LADO_MAXIMO_COMPRESION_PX = 1600;
const CALIDAD_COMPRESION = 0.72;

/**
 * Recomprime una imagen JPEG/WEBP si supera el umbral — NUNCA un PDF (el
 * `<canvas>` no sabe decodificarlo, y no hace falta: una evidencia offline
 * siempre es una foto de cámara/galería, nunca un documento). Best-effort:
 * cualquier fallo del pipeline canvas (navegador sin soporte, imagen
 * corrupta) devuelve el fichero original tal cual — comprimir es una
 * optimización, nunca un requisito para poder subir la evidencia.
 */
export async function comprimirSiProcede(archivo: Blob, mime: string): Promise<Blob> {
  if (!mime.startsWith('image/') || mime === 'image/svg+xml' || archivo.size <= UMBRAL_COMPRESION_BYTES) {
    return archivo;
  }
  if (typeof createImageBitmap === 'undefined' || typeof OffscreenCanvas === 'undefined') {
    return archivo;
  }
  try {
    const mapaBits = await createImageBitmap(archivo);
    const escala = Math.min(1, LADO_MAXIMO_COMPRESION_PX / Math.max(mapaBits.width, mapaBits.height));
    const ancho = Math.round(mapaBits.width * escala);
    const alto = Math.round(mapaBits.height * escala);
    const lienzo = new OffscreenCanvas(ancho, alto);
    const contexto = lienzo.getContext('2d');
    if (contexto === null) return archivo;
    contexto.drawImage(mapaBits, 0, 0, ancho, alto);
    const comprimido = await lienzo.convertToBlob({ type: 'image/jpeg', quality: CALIDAD_COMPRESION });
    //  Sólo se adopta si de verdad pesa menos — una imagen ya muy comprimida
    //  puede volverse más pesada al reescribirla en JPEG.
    return comprimido.size < archivo.size ? comprimido : archivo;
  } catch {
    return archivo;
  }
}

export async function calcularHashSha256(archivo: Blob): Promise<string> {
  const buffer = await archivo.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * §17 — cuota: mejor esfuerzo. `navigator.storage.estimate()` no está
 * disponible en todos los navegadores (Safari lo tiene parcialmente,
 * contextos privados pueden no darlo): la ausencia NUNCA bloquea subir una
 * evidencia, sólo se comprueba cuando el dato existe de verdad.
 */
export async function hayEspacioSuficiente(bytesNecesarios: number): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('storage' in navigator) || navigator.storage.estimate === undefined) {
    return true;
  }
  try {
    const { usage, quota } = await navigator.storage.estimate();
    if (usage === undefined || quota === undefined) return true;
    //  Margen de seguridad: nunca dejar el dispositivo al límite exacto —
    //  otras apps y el resto del propio almacén offline también necesitan sitio.
    const MARGEN = 5 * 1024 * 1024;
    return quota - usage - bytesNecesarios > MARGEN;
  } catch {
    return true;
  }
}

interface DatosNuevaMutacionEvidenciaComun {
  readonly idOt: string;
  readonly contexto: string;
  readonly descripcion: string | null;
  /** ISO — cuándo se tomó la foto; `null` si no se conoce con precisión. */
  readonly tomadaEn: string | null;
  readonly visibilidad: 'INTERNA' | 'CLIENTE';
  readonly archivo: Blob;
  readonly nombreArchivo: string;
  readonly fechaEfectiva: string;
  /** F14.2 — mismo criterio que `DatosNuevaMutacion` (reintento sin respuesta de un envío en línea). */
  readonly idOperacionCliente?: string;
  readonly rotuloLocal?: string | null;
}

export type DatosNuevaMutacionEvidencia =
  | (DatosNuevaMutacionEvidenciaComun & { readonly ambito: 'OT' })
  /** `propia`: F14.2 — foto del operario en SU tarea (ruta propia, siempre INTERNA; ver `PayloadEvidenciaSubir`). */
  | (DatosNuevaMutacionEvidenciaComun & { readonly ambito: 'LINEA'; readonly idLinea: string; readonly propia?: boolean })
  | (DatosNuevaMutacionEvidenciaComun & { readonly ambito: 'NC'; readonly idOperacionClienteNc: string });

/**
 * §9-§18 FASE 5.4: captura de evidencia offline. Hash SHA-256 y compresión
 * ocurren AQUÍ, antes de encolar — nunca al sincronizar (el dispositivo
 * puede apagarse entre medias; lo que se guarda en `evidenciaBlobs` ya es
 * el fichero final, byte a byte lo que se subirá). La dependencia hacia una
 * NC creada offline (ámbito NC) usa el mismo `resolverDestinoNc` que las
 * transiciones NC — la evidencia nunca sube antes de que su NC tenga un id
 * real (§4), y el bloqueo se detecta aquí, antes de gastar hash/compresión
 * en un fichero que no se podría encolar.
 */
export async function crearMutacionEvidencia(
  idPerfil: string,
  roles: readonly { readonly rol: string }[],
  datos: DatosNuevaMutacionEvidencia,
): Promise<ResultadoCrearMutacion> {
  //  F14.2 — el OPERARIO sólo adjunta fotos a SUS tareas, por su ruta propia (la misma que usa en línea).
  const propia = datos.ambito === 'LINEA' && datos.propia === true;
  const capacidadEvidencia = (r: readonly { readonly rol: string }[]): Rechazo | null =>
    propia && r.some((x) => x.rol === 'OPERARIO' || x.rol === 'ENCARGADO') ? null : exigirEncargado(r);
  const faltaRol = capacidadEvidencia(roles);
  if (faltaRol !== null) return faltaRol;
  const unidad: UnidadTrabajo =
    datos.ambito === 'LINEA' ? { tipo: 'LINEA', idLinea: datos.idLinea } : datos.ambito === 'NC' ? { tipo: 'NC' } : { tipo: 'OT' };
  const caducidad = await comprobarCaducidad(idPerfil, datos.idOt, unidad, capacidadEvidencia);
  if (!caducidad.ok) return caducidad;

  const detalleOt = await leerOtLocal(idPerfil, datos.idOt);
  if (detalleOt === null) {
    return { ok: false, motivo: 'Esa orden de trabajo no está disponible sin conexión.' };
  }
  if (ESTADOS_OT_TERMINALES.has(detalleOt.ot.estado)) {
    return { ok: false, motivo: `La OT está ${detalleOt.ot.estado} y no admite nuevas evidencias.` };
  }
  if (datos.archivo.size === 0) {
    return { ok: false, motivo: 'El fichero está vacío.' };
  }
  if (datos.archivo.size > LIMITE_EVIDENCIA_BYTES) {
    return { ok: false, motivo: `El fichero pesa más de ${String(Math.floor(LIMITE_EVIDENCIA_BYTES / (1024 * 1024)))} MB.` };
  }

  let idEntidad: string;
  let dependeDe: string | null = null;
  if (datos.ambito === 'LINEA') {
    const linea = await leerLineaLocal(idPerfil, datos.idLinea);
    if (linea === null || linea.idOt !== datos.idOt) {
      return { ok: false, motivo: 'Esa línea no está disponible sin conexión.' };
    }
    idEntidad = datos.idLinea;
  } else if (datos.ambito === 'NC') {
    const destino = await resolverDestinoNc(idPerfil, datos.idOperacionClienteNc);
    if (!destino.ok) return destino;
    idEntidad = destino.idEntidad;
    dependeDe = destino.dependeDe;
  } else {
    idEntidad = datos.idOt;
  }

  //  §17 — cuota ANTES de gastar tiempo en comprimir/hashear un fichero que
  //  luego no cabría.
  if (!(await hayEspacioSuficiente(datos.archivo.size))) {
    return { ok: false, motivo: 'No hay espacio suficiente en este dispositivo para guardar la evidencia.' };
  }

  const mime = datos.archivo.type !== '' ? datos.archivo.type : 'application/octet-stream';
  const archivoFinal = await comprimirSiProcede(datos.archivo, mime);
  const hashSha256 = await calcularHashSha256(archivoFinal);

  const payload: PayloadEvidenciaSubir = {
    ambito: datos.ambito,
    contexto: datos.contexto,
    descripcion: datos.descripcion,
    tomadaEn: datos.tomadaEn,
    visibilidad: propia ? 'INTERNA' : datos.visibilidad,
    hashSha256,
    mime: archivoFinal.type !== '' ? archivoFinal.type : mime,
    tamanoBytes: archivoFinal.size,
    nombreArchivo: datos.nombreArchivo,
    ...(propia ? { rutaPropia: true } : {}),
  };
  const mutacion = await insertarMutacion(idPerfil, {
    tipo: 'EVIDENCIA_SUBIR',
    idEntidad,
    idOt: datos.idOt,
    idCentro: detalleOt.ot.idCentro,
    payload,
    revisionBase: null,
    fechaEfectiva: datos.fechaEfectiva,
    dependeDe,
    ...(datos.idOperacionCliente === undefined ? {} : { idOperacionCliente: datos.idOperacionCliente }),
    rotuloLocal: datos.rotuloLocal ?? null,
  });
  await guardarBlobEvidencia(idPerfil, mutacion.idOperacionCliente, archivoFinal);
  dispararSyncSiOnline(idPerfil);
  return { ok: true, mutacion, aviso: caducidad.aviso };
}
